import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Divider, Paper, Skeleton, Stack, Typography, alpha, useTheme,
} from '@mui/material';
import { AccountBalanceOutlined, ChevronRight } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../../../services/financeService';
import { BankAccountReconciliation } from '../../../../types/finance';
import { useAuth } from '../../../../context/AuthContext';
import { useTimezone } from '../../../../hooks/useTimezone';
import { useModulePermissions } from '../../../../hooks/useModulePermissions';
import { formatPaisa } from '../../../../utils/paisa';
import EmptyState from '../../../common/EmptyState';
import { ToneChip } from '../../../common/StatusChip';
import Amount from '../../ledger/Amount';
import StatementDialog from './StatementDialog';

/**
 * Every bank account with where its reconciliation stands: the statement it
 * was last matched to, one in progress, and the completed ones before.
 */
const ReconciliationOverview: React.FC<{ onOpen: (id: string) => void }> = ({ onOpen }) => {
  const theme = useTheme();
  const { currency } = useAuth();
  const { formatDate } = useTimezone();
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const editable = canWrite('bank_reconciliation');
  const [accounts, setAccounts] = useState<BankAccountReconciliation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<BankAccountReconciliation | null>(null);

  const load = useCallback(async () => {
    try {
      setAccounts(await financeService.getReconciliations());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the bank accounts.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  if (!accounts) return <Stack spacing={1.5}>{[0, 1].map(i => <Skeleton key={i} variant="rounded" height={132} sx={{ borderRadius: 2 }} />)}</Stack>;
  if (!accounts.length) {
    return (
      <EmptyState
        icon={<AccountBalanceOutlined />}
        title="No bank accounts"
        description="Switch on a bank account under Finance setup → Chart of accounts to reconcile it here."
      />
    );
  }

  return (
    <Stack spacing={1.5}>
      {accounts.map(a => (
        <Paper key={a.accountId} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ p: 2.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontWeight: 500, mr: 1 }}>{a.code}</Box>
                {a.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {a.lastReconciled
                  ? <>Reconciled to {formatDate(a.lastReconciled.statementDate)} at {currency} {formatPaisa(a.lastReconciled.statementBalancePaisa)}</>
                  : 'Never reconciled'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: { sm: 'right' } }}>
              <Typography variant="caption" color="text.secondary" display="block">Balance in the books</Typography>
              <Amount paisa={a.balancePaisa} blankZero={false} variant="h6" fontWeight={700} color={a.balancePaisa < 0 ? 'error.main' : undefined} />
            </Box>
            {a.open ? (
              <Button variant="contained" disableElevation onClick={() => onOpen(a.open!._id)} sx={{ minWidth: 150 }}>Continue</Button>
            ) : editable && (
              <Button variant="outlined" onClick={() => setStarting(a)} sx={{ minWidth: 150 }}>Reconcile</Button>
            )}
          </Stack>

          {a.open && (
            <Stack direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 2.5, py: 1.25, bgcolor: alpha(theme.palette.warning.main, 0.06), borderTop: 1, borderColor: 'divider' }}>
              <ToneChip tone="pending" label="In progress" size="small" />
              <Typography variant="body2" color="text.secondary">
                Statement to {formatDate(a.open.statementDate)}, closing at {currency} {formatPaisa(a.open.statementBalancePaisa)}
              </Typography>
            </Stack>
          )}

          {a.history.length > 0 && (
            <>
              <Divider />
              <Box sx={{ px: 1, py: 0.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.disabled"
                  sx={{ display: 'block', px: 1.5, pt: 1, pb: 0.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Completed
                </Typography>
                {a.history.map(h => (
                  <Stack key={h._id} direction="row" alignItems="center" spacing={2} onClick={() => onOpen(h._id)}
                    sx={{ px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>Statement to {formatDate(h.statementDate)}</Typography>
                    <Amount paisa={h.statementBalancePaisa} blankZero={false} />
                    <Typography variant="caption" color="text.secondary" sx={{ width: 140, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                      Completed {formatDate(h.completedAt)}
                    </Typography>
                    <ChevronRight fontSize="small" color="action" />
                  </Stack>
                ))}
              </Box>
            </>
          )}
        </Paper>
      ))}

      <StatementDialog
        open={!!starting}
        title={`Reconcile ${starting?.name ?? ''}`}
        subtitle="Enter the statement you are matching the books to."
        after={starting?.lastReconciled?.statementDate}
        submitLabel="Start"
        onClose={() => setStarting(null)}
        onSubmit={async statement => {
          const rec = await financeService.startReconciliation({ accountId: starting!.accountId, ...statement });
          enqueueSnackbar('Reconciliation started', { variant: 'success' });
          onOpen(rec._id);
        }}
      />
    </Stack>
  );
};

export default ReconciliationOverview;
